/**
 * ClientPortalRouter - Feature Flag Aware Routing for Client Portals
 * 
 * When CLIENT_PORTAL_UNIFIED is enabled:
 * - /upload/:reportId redirects to /portal/:token (with conversion)
 * - /clarify/:token redirects to /portal/:token
 * 
 * When disabled:
 * - Old routes work as before
 * 
 * This enables parallel testing of the unified portal.
 */

import React from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
import { useFeatureFlag } from '../../hooks/useFeatureFlag';
import ClientUploadPortal from '../../pages/ClientUploadPortal';
import ClientClarificationPortal from '../../pages/ClientClarificationPortal';
import UnifiedClientPortal from '../../pages/UnifiedClientPortal';

/**
 * Upload Portal Router
 * Routes /upload/:reportId to either legacy or unified portal
 */
export function UploadPortalRouter() {
  const useUnified = useFeatureFlag('CLIENT_PORTAL_UNIFIED');
  const { reportId } = useParams<{ reportId: string }>();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  if (useUnified && reportId && token) {
    // Redirect to the unified portal using the real magic token as
    // the path segment. The backend /portal/{token} endpoint now
    // strictly validates against portal_access / clarification_requests
    // (audit 2026-04-19 PM) — a raw report_id is no longer accepted
    // in place of a token, so we must forward the real token here.
    return <Navigate to={`/portal/${token}?source=upload`} replace />;
  }

  // Legacy behavior — ClientUploadPortal reads ?token= directly. When
  // the unified flag is on but no token is present, fall through to
  // the legacy component which renders the missing-token error.
  return <ClientUploadPortal />;
}

/**
 * Clarification Portal Router
 * Routes /clarify/:token to either legacy or unified portal
 */
export function ClarificationPortalRouter() {
  const useUnified = useFeatureFlag('CLIENT_PORTAL_UNIFIED');
  const { token } = useParams<{ token: string }>();
  
  if (useUnified && token) {
    // Redirect to unified portal
    return <Navigate to={`/portal/${token}?source=clarify`} replace />;
  }
  
  // Legacy behavior
  return <ClientClarificationPortal />;
}

/**
 * Unified Portal - Always renders the unified version
 * This is the target for all client portal routes when flag is enabled
 */
export function UnifiedPortalRouter() {
  // Always use the unified portal on this route
  return <UnifiedClientPortal />;
}

export default {
  UploadPortalRouter,
  ClarificationPortalRouter,
  UnifiedPortalRouter,
};
